import { CMS_MENU_LIMIT, CMS_PAGE_LIMIT } from '@shared/cms/limits';
import type {
  SiteCmsBlock,
  SiteCmsDraftResponse,
  SiteCmsMenuItem,
  SiteCmsPageDraft,
  SiteCmsRichNode,
} from '@shared/types/entities';

import { nextCmsPageSlug } from './site-cms-editor-utils';
import { cmsMenuCount } from './site-cms-workspace-model';

export function cmsPageInMenu(items: readonly SiteCmsMenuItem[], slug: string): boolean {
  return items.some(item => (item.type === 'PAGE' && item.slug === slug) || cmsPageInMenu(item.children, slug));
}
export function cmsPageCounts(draft: SiteCmsDraftResponse) {
  return {
    all: draft.pages.length,
    included: draft.pages.filter(p => p.visibility !== 'DRAFT').length,
    private: draft.pages.filter(p => p.visibility === 'DRAFT').length,
    unlisted: draft.pages.filter(p => !cmsPageInMenu(draft.headerMenu, p.slug)).length,
  };
}
export function cmsAddPageToMenu(draft: SiteCmsDraftResponse, slug: string): SiteCmsDraftResponse {
  const page = draft.pages.find(p => p.slug === slug);
  if (!page) throw new Error('Page introuvable.');
  if (page.visibility === 'DRAFT')
    throw new Error('Incluez d’abord la page à la prochaine publication avant de l’ajouter au menu.');
  if (cmsPageInMenu(draft.headerMenu, slug)) return draft;
  if (cmsMenuCount(draft.headerMenu) >= CMS_MENU_LIMIT) throw new Error('Le menu a atteint sa limite de 60 entrées.');
  return {
    ...draft,
    headerMenu: [
      ...draft.headerMenu,
      { type: 'PAGE', label: page.title.trim().slice(0, 90) || 'Page', slug, children: [] },
    ],
  };
}
/** Editor/discovery order only, never silently reorders the public navigation. */
export function cmsMovePage(draft: SiteCmsDraftResponse, slug: string, direction: -1 | 1): SiteCmsDraftResponse {
  if (slug === 'home') return draft;
  const pages = draft.pages.filter(p => p.slug !== 'home');
  const index = pages.findIndex(p => p.slug === slug),
    to = index + direction;
  if (index < 0 || to < 0 || to >= pages.length) return draft;
  [pages[index], pages[to]] = [pages[to], pages[index]];
  return { ...draft, pages: [...draft.pages.filter(p => p.slug === 'home'), ...pages] };
}
/** A duplicate is private, unlisted, with independent blocks and local self-links. */
export function cmsDuplicatePage(
  draft: SiteCmsDraftResponse,
  slug: string,
): { draft: SiteCmsDraftResponse; page: SiteCmsPageDraft } {
  if (draft.pages.length >= CMS_PAGE_LIMIT) throw new Error('Maximum 30 pages.');
  const source = draft.pages.find(p => p.slug === slug);
  if (!source) throw new Error('Page introuvable.');
  const title = `${source.title.slice(0, 132)} (copie)`;
  const nextSlug = nextCmsPageSlug(`${source.slug === 'home' ? 'accueil' : source.slug}-copie`, draft.pages);
  function href(value: string): string {
    const split = value.search(/[?#]/),
      clean = split < 0 ? value : value.slice(0, split),
      suffix = split < 0 ? '' : value.slice(split);
    return clean === `/${slug}/` || clean === `/${slug}` || (slug === 'home' && clean === '/')
      ? `/${nextSlug}/${suffix}`
      : value;
  }
  function rich(node: SiteCmsRichNode): SiteCmsRichNode {
    return {
      ...node,
      ...(node.marks
        ? {
            marks: node.marks.map(m =>
              m.type === 'link' && m.attrs ? { ...m, attrs: { href: href(m.attrs.href) } } : m,
            ),
          }
        : {}),
      ...(node.content ? { content: node.content.map(rich) } : {}),
    };
  }
  function block(b: SiteCmsBlock): SiteCmsBlock {
    if (b.type === 'button' || b.type === 'cover' || b.type === 'cta') return { ...b, href: href(b.href) };
    if (b.type === 'partners') return { ...b, items: b.items.map(item => ({ ...item, url: href(item.url) })) };
    if (b.type === 'text' || b.type === 'imageText')
      return {
        ...b,
        ...(b.rich ? { rich: rich(b.rich) } : {}),
        text: b.text.replace(
          /\[([^\]]+)\]\(([^\s)]+)\)/g,
          (_, label: string, target: string) => `[${label}](${href(target)})`,
        ),
      };
    return b;
  }
  const page: SiteCmsPageDraft = {
    ...structuredClone(source),
    title,
    slug: nextSlug,
    visibility: 'DRAFT',
    blocks: structuredClone(source.blocks).map(block),
  };
  const at = draft.pages.findIndex(p => p.slug === slug) + 1;
  return { draft: { ...draft, pages: [...draft.pages.slice(0, at), page, ...draft.pages.slice(at)] }, page };
}
